import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { useEffect } from "react";

export default function Pricing() {
  useEffect(() => {
    if (window.fbq) window.fbq('track', 'ViewContent', { content_name: 'Pricing Page' });
  }, []);

  return (
    <div className="min-h-screen">
      <MarketingHeader />
      <div className="max-w-7xl mx-auto px-6 py-16">

        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            Free plan available — no credit card required
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-wide">
            Simple, Role-Based Pricing
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Start free or choose a paid plan. No credit card required for the free plan.
          </p>
        </div>

        {/* Shipper pricing */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-2xl font-bold mb-6 text-center">For Business Owners & Shippers</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Free</CardTitle>
                <div className="text-4xl font-bold">$0<span className="text-lg font-normal text-muted-foreground">/month</span></div>
                <p className="text-sm text-muted-foreground">3 shipments per month</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {[
                    "Post once — your partners are notified automatically",
                    "3 shipments per month",
                    "Receive and compare bids in one place",
                    "All communications in one Shipment Workspace",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button className="w-full" variant="outline"
                  onClick={() => window.location.href = "/register?role=shipper"}
                  data-testid="button-shipper-free">
                  Start Free
                </Button>
              </CardContent>
            </Card>
            <Card className="border-primary border-2">
              <CardHeader>
                <CardTitle>Unlimited</CardTitle>
                <div className="text-4xl font-bold">$25<span className="text-lg font-normal text-muted-foreground">/month</span></div>
                <p className="text-sm text-muted-foreground">Unlimited shipments</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {[
                    "Post once — your partners are notified automatically",
                    "Post unlimited shipments",
                    "Receive bids from your partners and the public board",
                    "Compare bids side by side",
                    "All communications in one Shipment Workspace",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button className="w-full"
                  onClick={() => window.location.href = "/register?role=shipper"}
                  data-testid="button-shipper-paid">
                  Get Started
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Carrier pricing */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-2xl font-bold mb-6 text-center">For Carriers</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Free</CardTitle>
                <div className="text-4xl font-bold">$0<span className="text-lg font-normal text-muted-foreground">/month</span></div>
                <p className="text-sm text-muted-foreground">Bid on invited loads only</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {[
                    "Bid on loads you're invited to",
                    "All communications in one Shipment Workspace",
                    "Build your reputation",
                    "Receive load notifications from shippers",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button className="w-full" variant="outline"
                  onClick={() => window.location.href = "/register?role=carrier"}
                  data-testid="button-carrier-free">
                  Start Free
                </Button>
              </CardContent>
            </Card>
            <Card className="border-primary border-2">
              <CardHeader>
                <Badge className="w-fit mb-2">Most Popular</Badge>
                <CardTitle>Search & Bid</CardTitle>
                <div className="text-4xl font-bold">$25<span className="text-lg font-normal text-muted-foreground">/month</span></div>
                <p className="text-sm text-muted-foreground">Full board access</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {[
                    "Everything in Free",
                    "Search the full public load board",
                    "Bid on any available load",
                    "Advanced load filters",
                    "All communications in one Shipment Workspace",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button className="w-full"
                  onClick={() => window.location.href = "/register?role=carrier"}
                  data-testid="button-carrier-search-bid">
                  Get Started
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Broker pricing */}
        <div className="max-w-6xl mx-auto mb-16">
          <h2 className="text-2xl font-bold mb-6 text-center">For Brokers</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Free</CardTitle>
                <div className="text-4xl font-bold">$0<span className="text-lg font-normal text-muted-foreground">/month</span></div>
                <p className="text-sm text-muted-foreground">Bid on invited loads only</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {[
                    "Bid on loads you're invited to",
                    "All communications in one Shipment Workspace",
                    "Build your reputation",
                    "Receive load notifications from shippers",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button className="w-full" variant="outline"
                  onClick={() => window.location.href = "/register?role=broker"}
                  data-testid="button-broker-free">
                  Start Free
                </Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Search & Bid</CardTitle>
                <div className="text-4xl font-bold">$25<span className="text-lg font-normal text-muted-foreground">/month</span></div>
                <p className="text-sm text-muted-foreground">Full board access</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {[
                    "Everything in Free",
                    "Search the full public load board",
                    "Bid on any available load",
                    "Advanced load filters",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button className="w-full" variant="outline"
                  onClick={() => window.location.href = "/register?role=broker"}
                  data-testid="button-broker-search-bid">
                  Get Started
                </Button>
              </CardContent>
            </Card>
            <Card className="border-primary border-2">
              <CardHeader>
                <CardTitle>Search, Bid & Post</CardTitle>
                <div className="text-4xl font-bold">$45<span className="text-lg font-normal text-muted-foreground">/month</span></div>
                <p className="text-sm text-muted-foreground">Full platform access</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {[
                    "Everything in Search & Bid",
                    "Post loads on behalf of your shipper clients",
                    "Full marketplace toolset",
                    "All communications in one Shipment Workspace",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button className="w-full"
                  onClick={() => window.location.href = "/register?role=broker"}
                  data-testid="button-broker-search-bid-post">
                  Get Started
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Stats strip */}
        <div className="bg-primary/5 rounded-md p-8 mb-16 max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">Free</div>
              <p className="text-sm text-muted-foreground">No credit card required</p>
              <p className="text-xs text-muted-foreground mt-2">Start free, upgrade anytime</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">0%</div>
              <p className="text-sm text-muted-foreground">Commission on loads</p>
              <p className="text-xs text-muted-foreground mt-2">Keep 100% of what you negotiate</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">$25</div>
              <p className="text-sm text-muted-foreground">Starting price for paid plans</p>
              <p className="text-xs text-muted-foreground mt-2">Upgrade when you're ready</p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-6xl mx-auto mb-16">
          <h2 className="text-2xl font-bold mb-6 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Do you have different membership levels?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Yes. Every role has a free tier and paid options. Shippers can post 3 shipments per month for free, or upgrade to $25/month for unlimited posts. Carriers and brokers can bid on invited loads for free, or upgrade to Search & Bid at $25/month for full board access. Brokers can also upgrade to Search, Bid & Post at $45/month to post loads.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Do I need a credit card to sign up?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  No credit card is required for the free plan. You only need to provide payment information when upgrading to a paid plan.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Do you take a percentage of my freight rates?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  <strong>No.</strong> Unlike most freight platforms, we charge a simple subscription fee. Whatever rate you negotiate is between you and the other party — we don't take a cut. Your pricing stays private and you keep 100% of what you earn.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What payment methods do you accept?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We accept all major credit cards (Visa, Mastercard, American Express, Discover) through our secure payment processor Stripe. Your payment information is encrypted and never stored on our servers.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Can I upgrade or downgrade my plan?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Yes. You can upgrade or change your plan at any time from the Settings page in your dashboard.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-card border rounded-md p-8 text-center max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Post once, notify your partners, and compare bids — all in one place.
          </p>
          <Button size="lg"
            onClick={() => window.location.href = "/register"}
            data-testid="button-get-started-cta">
            Start Free Today
          </Button>
        </div>

      </div>
      <MarketingFooter />
    </div>
  );
}
